import { CalendarCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { Card } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/utils/cn'
import { formatNumber } from '@/utils/format'
import { formatDays, type LeaveBalance } from '../types'
import { leaveSwatch } from '../colours'
import { WORDING, type LeaveWording } from '../wording'

interface Props {
  balances: LeaveBalance[] | undefined
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  wording?: LeaveWording
}

/** One card per leave type: allowance, used (approved), pending and what can still be requested. */
export function BalanceCards({ balances, loading, error, onRetry, wording = 'leave' }: Props) {
  const { t } = useTranslation('leave')
  const w = WORDING[wording]
  if (error)
    return (
      <Card>
        <ErrorState compact onRetry={onRetry} />
      </Card>
    )
  if (loading || !balances) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-card" />
        ))}
      </div>
    )
  }
  if (balances.length === 0) {
    return (
      <Card>
        <EmptyState compact icon={<CalendarCheck />} title={t(w.noTypes)} description={t(w.noTypesHint)} />
      </Card>
    )
  }

  return (
    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label={t(w.balancesLabel)}>
      {balances.map((b) => {
        const total = b.allowance ?? 0
        const usedPct = total > 0 ? Math.min(100, (b.used / total) * 100) : 0
        const pendingPct = total > 0 ? Math.min(100 - usedPct, (b.pending / total) * 100) : 0
        return (
          <li key={b.typeId}>
            <Card className="h-full p-5">
              <div className="flex items-center gap-2">
                <span aria-hidden="true" className={cn('size-2.5 rounded-full', leaveSwatch[b.colour])} />
                <h3 className="truncate text-sm font-semibold text-ink">{b.typeName}</h3>
              </div>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-ink tabular">
                {b.remaining === null ? t('Unlimited') : formatDays(b.remaining)}
                {b.remaining !== null ? (
                  <span className="ml-1 text-xs font-medium text-muted">
                    {t('left of {{allowance}}', { allowance: formatNumber(b.allowance) })}
                  </span>
                ) : null}
              </p>
              {b.allowance !== null ? (
                <div
                  className="mt-3 flex h-2 overflow-hidden rounded-full bg-surface-sunken"
                  role="img"
                  aria-label={t('{{used}} used and {{pending}} pending of {{count}} days', {
                    count: b.allowance,
                    used: formatNumber(b.used),
                    pending: formatNumber(b.pending),
                  })}
                >
                  <span className={cn('h-full', leaveSwatch[b.colour])} style={{ width: `${usedPct}%` }} />
                  <span
                    className={cn('h-full opacity-40', leaveSwatch[b.colour])}
                    style={{ width: `${pendingPct}%` }}
                  />
                </div>
              ) : null}
              <dl className="mt-3 flex gap-4 text-xs text-muted">
                <div>
                  <dt className="inline">{t('Used')} </dt>
                  <dd className="inline font-medium text-ink-soft tabular">{formatNumber(b.used)}</dd>
                </div>
                <div>
                  <dt className="inline">{t('Pending')} </dt>
                  <dd className="inline font-medium text-ink-soft tabular">{formatNumber(b.pending)}</dd>
                </div>
              </dl>
            </Card>
          </li>
        )
      })}
    </ul>
  )
}
